import * as React from "react";
import "./styles.scss";
import {
  PolymorphicPlayerCard,
  PolymorphicPlayerCardWidget,
  PlayerCardWidgetOwner,
  PlayerCard,
  PlayerCardWidget,
  dummyPlayerCard,
  PlayerCardTemplateWidget,
  PlayerCardDefaultables,
} from "../../../../services/bedrock";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import WidgetSettings from "./Components/WidgetSettings";
import _ from "lodash";
import { Controller, FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import NewWidgetModal from "./Components/NewWidgetModal";
import { Button, Form, Input, Label } from "semantic-ui-react";
import { Prompt } from "react-router-dom";
import { deepDiff, DeepPartial } from "../../../../utils";
import PaginatedDropdown from "../../../../components/PaginatedDropdown";
import { CancelToken } from "axios";
import { PlayerCardSettingsContext } from "../../Contexts";
import DetailsPage from "../../DetailsPage";
import SendNotificationConfirmation from "../../SendNotificationConfirmation";
import { Alert } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";

type WidgetTypeOf<T> = T extends { widgets: (infer W)[] } ? W : never;
interface PlayerCardTabProps<TCard extends { widgets: any[] } = PolymorphicPlayerCard> {
  active: boolean;
  playerCard: TCard;
  onPlayerCardSaved: (playerCard: TCard) => void;
  onClose?: () => void;
  onViewMetrics?: (playerCardWidgetId: number) => void;
}

const PlayerCardTab: React.FC<PlayerCardTabProps> = ({
  active,
  playerCard,
  onPlayerCardSaved,
  onClose,
  onViewMetrics,
}) => {
  const [draggingWidgetId, setDraggingWidgetId] = useState<number>();
  const [expandedWidgetId, setExpandedWidgetId] = useState<number>();
  const [insertNewWidgetIndex, setInsertNewWidgetIndex] = useState<number>();
  const [showSendNotificationConfirmation, setShowSendNotificationConfirmation] = useState(false);

  const { Preview, Templates, Themes, PlayerCards, User } = useContext(PlayerCardSettingsContext);

  const methods = useForm<PolymorphicPlayerCard>({ defaultValues: playerCard });
  const {
    handleSubmit,
    control,
    formState: { isDirty, isSubmitting, defaultValues, dirtyFields },
    reset,
    getValues,
    setValue,
  } = methods;
  const {
    fields: widgetFields,
    append: appendWidget,
    update: updateWidget,
    remove: removeWidget,
  } = useFieldArray({
    control,
    keyName: "formId",
    name: "widgets",
  });

  const cancel = (close: boolean) => {
    if (!isDirty || window.confirm("Are you sure you want to discard your changes?")) {
      reset(playerCard);
      if (close) onClose?.();
    }
  };

  const watchedWidgets = useWatch({ control, name: "widgets" });
  const watchedTemplateId = useWatch({ control, name: "templateId" });
  const watchedThemeId = useWatch({ control, name: "themeId" });

  useEffect(() => {
    if (!active) return;
    const widgets = watchedWidgets as PlayerCardWidget[];
    if (playerCard.isTemplate) {
      Preview.setPlayerCard({ ...dummyPlayerCard, widgets });
    } else {
      const templateId = watchedTemplateId ?? PlayerCardDefaultables.unassigned.id;
      const template =
        templateId === PlayerCardDefaultables.default.id
          ? playerCard.defaultTemplate
          : templateId === PlayerCardDefaultables.unassigned.id
          ? null
          : Templates.cachedTemplates[templateId];
      setValue("template", template);

      const themeId = watchedThemeId ?? PlayerCardDefaultables.unassigned.id;
      const theme =
        themeId === PlayerCardDefaultables.default.id
          ? playerCard.defaultTheme
          : themeId === PlayerCardDefaultables.unassigned.id
          ? null
          : Themes.cachedThemes[themeId];
      setValue("theme", theme);

      const slug = defaultValues?.slug || playerCard.slug;

      const playerCardPreview = {
        ..._.cloneDeep(playerCard as PlayerCard),
        widgets,
        templateId,
        template,
        themeId,
        theme,
        slug,
      };

      Preview.setPlayerCard(playerCardPreview);
    }
  }, [
    active,
    watchedWidgets,
    watchedTemplateId,
    watchedThemeId,
    Templates.cachedTemplates,
    Themes.cachedThemes,
    defaultValues?.slug,
  ]);

  useEffect(() => {
    if (watchedTemplateId !== null) {
      const template = watchedTemplateId ? Templates.cachedTemplates[watchedTemplateId] : null;
      setValue("template", template);
    }
  }, [watchedTemplateId]);

  useEffect(() => {
    reset(playerCard, {
      keepErrors: true,
      keepDirty: true,
      keepDirtyValues: true,
      keepIsSubmitted: true,
      keepTouched: true,
      keepIsValid: true,
      keepSubmitCount: true,
    });
  }, [playerCard]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isDirty) event.preventDefault();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const saveChanges = useCallback(
    (sendNotification?: boolean) => {
      handleSubmit((data: PolymorphicPlayerCard) => {
        if (!playerCard) return;
        if (!isDirty) return;

        const changes = deepDiff(playerCard, data);
        const widgetChanges = data.widgets
          .map(widget => {
            const savedWidget = playerCard.widgets.find(w => w.id === widget.id);
            var widgetChanges: DeepPartial<PolymorphicPlayerCardWidget>;
            if (savedWidget) {
              widgetChanges = deepDiff(savedWidget, widget);
              if (Object.keys(widgetChanges).length === 0) return;
              widgetChanges.id = savedWidget.id;
            } else {
              widgetChanges = widget;
              delete widgetChanges.id;
            }
            if (widgetChanges.owner?.id) delete widgetChanges.owner.id;
            if (widgetChanges.owner?.type === "ContentPage") delete widgetChanges.owner.contentPage;
            if (widgetChanges.owner?.type === "Post") delete widgetChanges.owner.post;

            widgetChanges.templateFields?.forEach(field => {
              if (field?.id && field.id < 0) delete field.id;
            });

            return widgetChanges;
          })
          .filter(Boolean);

        if (data.isTemplate) {
          changes.widgets = widgetChanges as DeepPartial<PlayerCardTemplateWidget>[];
        } else {
          changes.widgets = widgetChanges as DeepPartial<PlayerCardWidget>[];
        }

        return PlayerCards.updatePolymorphicPlayerCard(playerCard, changes, sendNotification).then(playerCard => {
          onPlayerCardSaved(playerCard);
          reset(playerCard);
        });
      })();
    },
    [playerCard, isDirty]
  );

  const handleSaveButtonClick = () => {
    if (User.isPlayer || playerCard.isTemplate) {
      saveChanges();
    } else {
      handleSubmit(() => setShowSendNotificationConfirmation(true))();
    }
  };

  const widgetBaselineRef = useRef((playerCard.widgets ?? []).map(widget => _.cloneDeep(widget)));
  useEffect(() => {
    if (!isDirty) {
      widgetBaselineRef.current = (playerCard.widgets ?? []).map(widget => _.cloneDeep(widget));
    }
  }, [playerCard, isDirty]);

  const hasNonHeaderFooterWidgetChanges = useMemo(() => {
    if (!playerCard.isTemplate || !isDirty) return false;

    const isHeaderOrFooter = (type?: string) => type === "Header" || type === "Footer";
    const currentWidgets = watchedWidgets ?? [];
    const dirtyWidgets = dirtyFields.widgets;
    const widgetIsDirty = (index: number) => {
      if (dirtyWidgets == null) return false;
      if (dirtyWidgets === true) return true;
      return !!dirtyWidgets[index];
    };

    const hasDirtyBodyWidget = currentWidgets.some((widget, index) => {
      if (isHeaderOrFooter(widget.owner?.type)) return false;
      return widgetIsDirty(index);
    });
    if (hasDirtyBodyWidget) return true;

    const baseline = widgetBaselineRef.current;
    const snapshot = (widget: PolymorphicPlayerCardWidget) => ({
      isVisible: widget.isVisible,
      sortIndex: widget.sortIndex,
      owner: widget.owner,
      templateFields: "templateFields" in widget ? widget.templateFields : undefined,
    });

    const bodyWidgetChanged = currentWidgets.some(widget => {
      if (isHeaderOrFooter(widget.owner?.type)) return false;
      const savedWidget = baseline.find(w => w.id === widget.id);
      if (!savedWidget) return true;
      return !_.isEqual(snapshot(savedWidget), snapshot(widget));
    });
    if (bodyWidgetChanged) return true;

    return baseline.some(savedWidget => {
      if (isHeaderOrFooter(savedWidget.owner?.type)) return false;
      return !currentWidgets.some(widget => widget.id === savedWidget.id);
    });
  }, [playerCard.isTemplate, isDirty, watchedWidgets, dirtyFields.widgets]);

  const handleSendNotificationConfirmationClosed = useCallback(
    (sendNotification: boolean | undefined) => {
      setShowSendNotificationConfirmation(false);
      if (sendNotification !== undefined) {
        saveChanges(sendNotification);
      }
    },
    [saveChanges]
  );

  useEffect(() => {
    const dragOverEventHandler = (e: DragEvent) => e.preventDefault();
    document.addEventListener("dragover", dragOverEventHandler);
    return () => document.removeEventListener("dragover", dragOverEventHandler);
  }, []);

  const handleDragOver = (event: React.DragEvent, widgetId: number) => {
    event.preventDefault();

    if (!draggingWidgetId || widgetId === draggingWidgetId) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const isBottomHalf = event.clientY - rect.top > rect.height / 2;

    const widgets = getValues("widgets");

    const fromIndex = widgets.find(el => el.id === draggingWidgetId)?.sortIndex;
    const toIndex = widgets.find(el => el.id === widgetId)?.sortIndex;
    if (!fromIndex || !toIndex || fromIndex === toIndex) return;
    if (isBottomHalf && fromIndex > toIndex) return;
    if (!isBottomHalf && fromIndex < toIndex) return;

    const movingDown = fromIndex < toIndex;

    widgets.forEach((field, index) => {
      if (field.sortIndex === null) return;

      if (field.sortIndex === fromIndex) {
        setValue(`widgets.${index}.sortIndex`, toIndex, { shouldDirty: true });
      } else if (movingDown) {
        if (field.sortIndex > fromIndex && field.sortIndex <= toIndex) {
          setValue(`widgets.${index}.sortIndex`, field.sortIndex - 1, { shouldDirty: true });
        }
      } else {
        if (field.sortIndex >= toIndex && field.sortIndex < fromIndex) {
          setValue(`widgets.${index}.sortIndex`, field.sortIndex + 1, { shouldDirty: true });
        }
      }
    });
  };

  const handleNewWidgetOwnerSelected = useCallback(
    (widgetOwner: PlayerCardWidgetOwner) => {
      if (insertNewWidgetIndex === undefined) return;

      watchedWidgets.forEach((field, index) => {
        if (field.sortIndex === null) return;
        if (field.sortIndex >= insertNewWidgetIndex) {
          setValue(`widgets.${index}.sortIndex`, field.sortIndex + 1, { shouldDirty: true });
        }
      });
      const widget = {
        id: Date.now() * -1,
        isVisible: true,
        sortIndex: insertNewWidgetIndex,
        owner: widgetOwner,
        templateFields: [],
      };
      appendWidget(widget);
      setExpandedWidgetId(widget.id);
      setInsertNewWidgetIndex(undefined);
    },
    [watchedWidgets, insertNewWidgetIndex]
  );

  const handleRemoveWidget = useCallback(
    (index: number) => {
      const id = getValues(`widgets.${index}.id`);
      const sortIndex = getValues(`widgets.${index}.sortIndex`);
      if (id < 0) {
        removeWidget(index);
      } else {
        setValue(`widgets.${index}.sortIndex`, null, { shouldDirty: true });
      }

      if (sortIndex === null) return;

      watchedWidgets.forEach((field, index) => {
        if (field.sortIndex === null) return;
        if (field.sortIndex > sortIndex) {
          setValue(`widgets.${index}.sortIndex`, field.sortIndex - 1, { shouldDirty: true });
        }
      });
    },
    [watchedWidgets]
  );

  useEffect(() => Preview.setSelectedWidgetId(expandedWidgetId), [expandedWidgetId]);

  const loadTemplateData = async (page: number, searchQuery?: string, cancelToken?: CancelToken) => {
    return Templates.loadTemplateDropdownOptions(
      page,
      searchQuery,
      cancelToken,
      getValues("template"),
      getValues("defaultTemplate")
    );
  };

  const loadThemeData = async (page: number, searchQuery?: string, cancelToken?: CancelToken) => {
    return Themes.loadThemeDropdownOptions(
      page,
      searchQuery,
      cancelToken,
      getValues("theme"),
      getValues("defaultTheme")
    );
  };

  const content = (
    <div className={`playerCard ${active ? "active" : ""}`}>
      <Prompt when={isDirty} message="You have unsaved changes. Are you sure you want to leave this page?" />
      <NewWidgetModal
        open={insertNewWidgetIndex !== undefined}
        onClose={() => setInsertNewWidgetIndex(undefined)}
        onNewWidgetOwnerSelected={handleNewWidgetOwnerSelected}
      />
      <SendNotificationConfirmation
        open={showSendNotificationConfirmation}
        playerName={playerCard.isTemplate ? "Players" : playerCard.player.fullName ?? "Player"}
        onClose={handleSendNotificationConfirmationClosed}
      />
      <FormProvider {...methods}>
        <Form
          onKeyDown={(e: any) => {
            if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
              e.preventDefault();
            }
          }}
        >
          {playerCard.isTemplate && (
            <Controller
              name="name"
              control={control}
              rules={{ required: "This is required" }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Form.Field className="playerCardField playerCardTemplateName" error={!!error}>
                    <div className="playerCardFieldLabel">Name</div>
                    <Input {...field} placeholder="Enter Template Name..." />
                    {error && (
                      <Label basic prompt pointing>
                        {error.message}
                      </Label>
                    )}
                  </Form.Field>
                );
              }}
            />
          )}
          {User.isCoach && !playerCard.isTemplate && (
            <div className="templateAndThemeContainer">
              <Controller
                name="templateId"
                control={control}
                render={({ field, fieldState: { error } }) => {
                  const onChange = (_e: any, data: any) => field.onChange(data.value);
                  const { ref, ...effectiveField } = field;
                  return (
                    <Form.Field className="playerCardField" error={!!error}>
                      <div className="playerCardFieldLabel">Template</div>
                      <PaginatedDropdown
                        {...effectiveField}
                        search
                        onChange={onChange}
                        loadData={loadTemplateData}
                        placeholder="Select Template..."
                        disabled={!User.hasWritePermission}
                      />
                      {error && (
                        <Label basic prompt pointing>
                          {error.message}
                        </Label>
                      )}
                    </Form.Field>
                  );
                }}
              />
              <Controller
                name="themeId"
                control={control}
                render={({ field, fieldState: { error } }) => {
                  const onChange = (_e: any, data: any) => field.onChange(data.value);
                  const { ref, ...effectiveField } = field;

                  return (
                    <Form.Field className="playerCardField" error={!!error}>
                      <div className="playerCardFieldLabel">Theme</div>
                      <PaginatedDropdown
                        {...effectiveField}
                        search
                        onChange={onChange}
                        loadData={loadThemeData}
                        placeholder="Select Theme..."
                        disabled={!User.hasWritePermission}
                      />
                      {error && (
                        <Label basic prompt pointing>
                          {error.message}
                        </Label>
                      )}
                    </Form.Field>
                  );
                }}
              />
            </div>
          )}
          {(widgetFields?.length || 0) > 1 && User.hasWritePermission && (
            <Button
              className="addNewWidgetButton"
              primary
              onClick={() => setInsertNewWidgetIndex(1)}
              content="Add Widget"
            />
          )}
          <div className="widgets">
            {widgetFields?.map((widget, index) => {
              const effectiveSortIndex = getValues(`widgets.${index}.sortIndex`);
              if (effectiveSortIndex === null) return null;
              return (
                <WidgetSettings
                  key={widget.id}
                  index={index}
                  widget={widget}
                  onUpdate={(index, polymorphicWidget) =>
                    updateWidget(index, polymorphicWidget as WidgetTypeOf<PolymorphicPlayerCardWidget>)
                  }
                  onRemove={() => handleRemoveWidget(index)}
                  isExpanded={expandedWidgetId === widget.id}
                  isDragging={draggingWidgetId === widget.id}
                  onClick={() =>
                    setExpandedWidgetId(prev => (widgetFields[index].id === prev ? undefined : widgetFields[index].id))
                  }
                  onViewMetrics={() => onViewMetrics?.(widget.id)}
                  onInsertWidget={() => setInsertNewWidgetIndex(effectiveSortIndex + 1)}
                  onDragOver={event => handleDragOver(event, widget.id)}
                  onDragStart={() => setDraggingWidgetId(widget.id)}
                  onDragEnd={() => setDraggingWidgetId(undefined)}
                  onMouseEnter={() => Preview.setFocusedWidgetId(widget.id)}
                  onMouseLeave={() => Preview.setFocusedWidgetId(undefined)}
                />
              );
            })}
          </div>
          <div className="actions">
            <div className="buttons">
              <Button
                className="menuButton"
                loading={isSubmitting}
                disabled={!isDirty}
                secondary
                content="Cancel"
                onClick={() => cancel(false)}
              />
              {User.hasWritePermission && (
                <Button
                  className="menuButton"
                  loading={isSubmitting}
                  disabled={!isDirty}
                  primary
                  content="Save"
                  onClick={handleSaveButtonClick}
                />
              )}
            </div>
            {playerCard.isTemplate && hasNonHeaderFooterWidgetChanges && (
              <Alert variant="warning" className="widgetChangeWarning">
                <AlertTriangle />
                <Alert.Description>
                  Changes to widgets other than the Header and Footer will apply only to new Player Cards created from
                  this template. Existing Player Cards will not be affected.
                </Alert.Description>
              </Alert>
            )}
            {/* While this works, we aren't sure if we want to display it yet */}
            {/* <Checkbox
              className="showPlaceholdersCheckbox"
              checked={Placeholders.displayPlaceholders}
              onChange={() => Placeholders.setDisplayPlaceholders(!Placeholders.displayPlaceholders)}
              label="Show Placeholders"
            /> */}
          </div>
        </Form>
      </FormProvider>
    </div>
  );
  if (active && User.isCoach) {
    return <DetailsPage onClose={() => cancel(true)}>{content}</DetailsPage>;
  } else {
    return content;
  }
};
export default PlayerCardTab;
